// apps/web/src/sync/webSync.ts
// ─────────────────────────────────────────────────────────────────────────
// Web wiring for the sync engine. Connects the platform-agnostic core
// SyncEngineImpl to this app's concrete pieces:
//   • Firebase auth        → the bearer token + current user id
//   • WebStorageAdapter    → the Dexie-backed queue/operations tables
//   • WebNetworkAdapter    → navigator.onLine gating
//
// Import the `sync` singleton anywhere you need to trigger a sync, or use the
// thin `recordOperation` / `recordEconomy` helpers below to enqueue a single
// operation. Everything here is intentionally thin — the logic lives in core
// so the future Expo app can re-wire the same engine.
//
// PRODUCERS (load-bearing for the first full-stack row)
//   The engine only replicates what something records. Nothing in the app fed
//   it until these helpers existed. recordOperation() enqueues progress-style
//   ops (lesson.complete, quiz.complete, srs.review…); recordEconomy() enqueues
//   the economy.* ops that fan out to the /economy ledger. Both no-op cleanly
//   when signed out (the engine stamps userId at push time, but a recorded op
//   with no user can't be attributed, so we guard here).
// ─────────────────────────────────────────────────────────────────────────

import {
	HttpSyncClient,
	SyncEngineImpl,
	type BalanceResponseWire,
	type EconomyOpeningWire,
	type MigrateResponseWire,
	type NetworkState,
	type Operation,
	type OperationType,
	type PulledOperationWire,
	type TokenGetter,
} from '@nisk/core';
import type { User } from 'firebase/auth';
import { storage, network } from '../adapters';
import { auth } from '../lib/firebase';
import { store } from '../store/index';
import { reconcileXp } from '../store/xpSlice';
import { reconcileBalance } from '../store/economySlice';
import { reconcileBalanceFromServer } from '../lib/economy';
import { markLessonComplete } from '../shared/useProgress';

// API origin. Set VITE_API_BASE per environment (.env.local for dev, build env
// for prod). Falls back to the local Kestrel HTTP port from launchSettings.json
// (51856) so a developer without a .env.local still hits the running API rather
// than a dead port. Prod MUST set VITE_API_BASE explicitly.
const API_BASE: string =
	(import.meta.env.VITE_API_BASE as string | undefined) ??
	'http://localhost:51856';

// ── Device id ──────────────────────────────────────────────────────────────
// Stable per browser install. Persisted in localStorage so it survives reloads
// but is distinct per device — the server uses it to avoid echoing a device's
// own operations back to it on pull.
const DEVICE_ID_KEY = 'nisk.deviceId';

function getDeviceId(): string {
	try {
		let id = localStorage.getItem(DEVICE_ID_KEY);
		if (!id) {
			id = crypto.randomUUID();
			localStorage.setItem(DEVICE_ID_KEY, id);
		}
		return id;
	} catch {
		// Private mode / storage disabled — fall back to an ephemeral id.
		return crypto.randomUUID();
	}
}

const deviceId = getDeviceId();

// ── Token getter ────────────────────────────────────────────────────────────
// Bridges Firebase's getIdToken(forceRefresh) to the client's TokenGetter. On a
// 401 the client calls this with forceRefresh=true to mint a fresh token.
const getToken: TokenGetter = async (forceRefresh) => {
	const user = auth.currentUser;
	if (!user) return null;
	return user.getIdToken(forceRefresh);
};

// ── applyPulled ──────────────────────────────────────────────────────────────
// Called by the engine with operations pulled from OTHER devices, AFTER they've
// been persisted to the OPERATIONS table. This folds them into live derived
// state so a lesson completed on device B shows as complete on device A.
//
// IMPORTANT SCOPE: the /operations pull returns the operation LOG only. economy.*
// ops are rejected from that log server-side (the IsEconomy backstop), so they
// NEVER arrive here — cross-device XP/gems is reconciled separately from the
// authoritative balance endpoint (see reconcileBalances below), not from this
// path. Today the only progress op any producer actually records is
// lesson.complete; the other known types are handled as forward-compatible
// no-ops (dev-warn) so a future producer surfaces here loudly rather than
// silently dropping. All folds are idempotent (re-marking complete is a no-op).
async function applyPulled(ops: PulledOperationWire[]): Promise<void> {
	if (ops.length === 0) return;

	let applied = 0;
	const deferred: string[] = [];

	for (const op of ops) {
		switch (op.type) {
			case 'lesson.complete': {
				const { moduleId, lessonId } = resolveLessonRef(op);
				if (moduleId && lessonId) {
					// Writes the source-of-truth key AND emits the nis:progress
					// event every Learn view listens on, so completion shows
					// everywhere without a reload.
					markLessonComplete(moduleId, lessonId);
					applied += 1;
				} else if (import.meta.env.DEV) {
					console.warn(
						'[sync] pulled lesson.complete with unresolvable ref',
						op.entityId,
					);
				}
				break;
			}

			// Known progress types with no producer yet. When one is added,
			// wire its fold here — this keeps the gap visible instead of silent.
			case 'lesson.read':
			case 'lesson.listen_complete':
			case 'quiz.attempt':
			case 'quiz.complete':
			case 'srs.review':
			case 'flashcards.session_complete':
			case 'streak.advance':
			case 'level.up':
			case 'download.added':
			case 'download.removed':
			case 'settings.change':
				deferred.push(op.type);
				break;

			// economy.* never arrives via pull (see scope note above); if it
			// somehow does, ignore — reconcileBalances owns balances.
			default:
				break;
		}
	}

	if (import.meta.env.DEV && deferred.length > 0) {
		const counts = deferred.reduce<Record<string, number>>((acc, t) => {
			acc[t] = (acc[t] ?? 0) + 1;
			return acc;
		}, {});
		console.info(
			'[sync] pulled progress ops with no reconciler yet (no-op):',
			counts,
		);
	}

	// XP/gems may have moved on another device alongside this progress; refresh
	// the authoritative balances so the header reflects it promptly rather than
	// waiting for the next independent trigger.
	if (applied > 0) {
		await reconcileBalances();
	}
}

/**
 * Resolve a pulled lesson op to (moduleId, lessonId). Prefers the structured
 * payload the producer sends ({ moduleId, lessonId }); falls back to splitting
 * the "moduleId:lessonId" entityId convention if the payload is absent.
 */
function resolveLessonRef(op: PulledOperationWire): {
	moduleId: string | null;
	lessonId: string | null;
} {
	const p = op.payload;
	if (p !== null && typeof p === 'object') {
		const moduleId = (p as { moduleId?: unknown }).moduleId;
		const lessonId = (p as { lessonId?: unknown }).lessonId;
		if (typeof moduleId === 'string' && typeof lessonId === 'string') {
			return { moduleId, lessonId };
		}
	}
	// Fallback: entityId is recorded as `${moduleId}:${lessonId}`.
	const idx = op.entityId.indexOf(':');
	if (idx > 0 && idx < op.entityId.length - 1) {
		return {
			moduleId: op.entityId.slice(0, idx),
			lessonId: op.entityId.slice(idx + 1),
		};
	}
	return { moduleId: null, lessonId: null };
}

// ── Balance reconciliation ────────────────────────────────────────────────────
// economy.* ops don't round-trip through the operation log, so cross-device XP
// and gems can only be learned from the server's authoritative balance. This
// pulls GET /economy/balance and snaps both slices to it. Both reconcile paths
// are monotonic-guarded in their reducers (XP only ever raises; gems guards
// against malformed/negative), so a stale or racing read can't lower a balance.
// Safe to call on any post-auth trigger; no-ops cleanly when signed out.
export async function reconcileBalances(): Promise<void> {
	if (!auth.currentUser) return;

	let bal: BalanceResponseWire;
	try {
		bal = await client.getBalance();
	} catch (err: unknown) {
		console.warn('[sync] balance reconciliation failed', err);
		return;
	}

	// XP: reducer persists + recomputes level; ignores a non-raising value.
	store.dispatch(reconcileXp(bal.xp));

	// Gems: write durable storage first (lib/economy), then snap the live slice
	// to the returned GemBalance — the two-step the reconcileBalance reducer
	// documents.
	const nextBalance = reconcileBalanceFromServer(bal.gems);
	store.dispatch(reconcileBalance(nextBalance));
}

// ── One-time migration push ──────────────────────────────────────────────────
// The local→IndexedDB migration (migrateFromLocalStorage) carried the legacy
// XP/gems totals locally, but those balances were never sent to the server — the
// sync engine fans out economy.* OPERATIONS, and the old totals aren't ops, they
// were accumulated snapshots. Left alone, a migrating user's earned XP/gems would
// never reach the server economy ledger.
//
// This pushes the snapshot ONCE per device, after sign-in, as the migrate
// request's `opening` block. The server seeds it as an authoritative opening
// balance, and no-ops if the (user, currency) ledger already has rows — so a
// user who already earned on another device is never clobbered. Triple-guarded:
// the client flag below, the server's device-keyed already-migrated check, and
// the seed's ledger guard.
const MIGRATION_PUSHED_KEY = 'nisk.migration.serverPushed.v1';

// Legacy storage keys, read straight from localStorage (the Dexie migration
// copies rather than deletes, so these survive). XP: a bare number or {total}.
// Gems: GemBalance.gems under the economy balance key.
const LEGACY_XP_KEY = 'nis.gamification.xp';
const LEGACY_BALANCE_KEY = 'nis.economy.balance';

function readLegacyOpening(): {
	opening: EconomyOpeningWire;
	legacyKeysFound: string[];
} {
	const legacyKeysFound: string[] = [];
	let xp = 0;
	let gems = 0;

	try {
		const rawXp = localStorage.getItem(LEGACY_XP_KEY);
		if (rawXp !== null) {
			legacyKeysFound.push(LEGACY_XP_KEY);
			const parsed: unknown = JSON.parse(rawXp);
			if (typeof parsed === 'number') {
				xp = parsed;
			} else if (
				parsed !== null &&
				typeof parsed === 'object' &&
				typeof (parsed as { total?: unknown }).total === 'number'
			) {
				xp = (parsed as { total: number }).total;
			}
		}
	} catch {
		/* malformed legacy XP — treat as nothing to seed */
	}

	try {
		const rawBal = localStorage.getItem(LEGACY_BALANCE_KEY);
		if (rawBal !== null) {
			legacyKeysFound.push(LEGACY_BALANCE_KEY);
			const parsed: unknown = JSON.parse(rawBal);
			if (
				parsed !== null &&
				typeof parsed === 'object' &&
				typeof (parsed as { gems?: unknown }).gems === 'number'
			) {
				gems = (parsed as { gems: number }).gems;
			}
		}
	} catch {
		/* malformed legacy balance — treat as nothing to seed */
	}

	// Negative/NaN snapshots are nonsense; floor at 0. The server also rejects
	// non-positive seeds, but normalizing here keeps the wire honest.
	xp = Number.isFinite(xp) && xp > 0 ? Math.floor(xp) : 0;
	gems = Number.isFinite(gems) && gems > 0 ? Math.floor(gems) : 0;

	return { opening: { xp, gems }, legacyKeysFound };
}

/**
 * Push the legacy economy snapshot to the server exactly once per device, after
 * sign-in. No-ops when signed out, when already pushed, or when there's nothing
 * to seed. Safe to call on every auth resolution — the flag gates re-entry.
 */
export async function runMigrationOnce(): Promise<void> {
	if (!auth.currentUser) return;

	try {
		if (localStorage.getItem(MIGRATION_PUSHED_KEY)) return;
	} catch {
		// Storage unavailable (private mode): skip rather than risk a re-push
		// loop. The server's device-keyed guard still protects correctness.
		return;
	}

	const { opening, legacyKeysFound } = readLegacyOpening();

	// Nothing to carry over — mark done so we don't re-check every boot.
	if (opening.xp === 0 && opening.gems === 0) {
		try {
			localStorage.setItem(
				MIGRATION_PUSHED_KEY,
				new Date().toISOString(),
			);
		} catch {
			/* noop */
		}
		return;
	}

	try {
		const resp: MigrateResponseWire = await client.migrate({
			deviceId,
			legacyKeysFound,
			operations: [], // non-economy op replay is handled by live producers
			opening,
		});

		// Mark pushed only on a definitive server response (success or
		// already-migrated). A thrown error leaves the flag unset so the next
		// boot retries — the seed is idempotent, so a retry is safe.
		try {
			localStorage.setItem(
				MIGRATION_PUSHED_KEY,
				new Date().toISOString(),
			);
		} catch {
			/* noop */
		}

		if (import.meta.env.DEV) {
			console.info(
				`[sync] migration push complete: seededXp=${resp.seededXp} ` +
					`seededGems=${resp.seededGems} alreadyMigrated=${resp.alreadyMigrated}`,
			);
		}
	} catch (err: unknown) {
		// Leave the flag unset; retried on next sign-in/boot.
		console.warn('[sync] migration push failed; will retry next boot', err);
	}
}

// ── Wiring ───────────────────────────────────────────────────────────────────

const client = new HttpSyncClient({
	baseUrl: API_BASE,
	getToken,
});

export const syncClient = client;

export const sync = new SyncEngineImpl({
	storage,
	network,
	client,
	deviceId,
	getUserId: () => auth.currentUser?.uid ?? null,
	applyPulled,
});

// ── Producer helpers ─────────────────────────────────────────────────────────
// Thin wrappers over sync.record(...) that stamp userId/deviceId and trigger a
// best-effort push. Callers (thunks, components) use these instead of touching
// the engine's record() shape directly.

/**
 * Enqueue a progress-style operation (lesson.complete, quiz.complete,
 * srs.review, etc.). No-ops when signed out. Fires a non-blocking push so the
 * row lands promptly; failures are swallowed (offline-first retries later).
 */
export function recordOperation<TPayload>(
	type: OperationType,
	entityId: string,
	payload: TPayload,
): void {
	const userId = auth.currentUser?.uid ?? null;
	if (!userId) return;

	const op: Omit<Operation<TPayload>, 'id' | 'clientTimestamp' | 'status'> = {
		userId,
		deviceId,
		type,
		entityId,
		payload,
	};

	void sync
		.record(op)
		.then(() => sync.sync())
		.catch((err: unknown) => {
			console.warn('[sync] recordOperation failed', type, entityId, err);
		});
}

/**
 * Enqueue an economy operation. The engine fans economy.* ops out to the
 * /economy ledger endpoint (not the operation log). `amount` is the credited
 * magnitude; `currency` defaults to 'xp' on the wire but gems is the common
 * case here, so callers pass it explicitly. `receipt` only applies to iap.
 */
export function recordEconomy(args: {
	type: 'economy.earn' | 'economy.spend' | 'economy.iap_purchase';
	entityId: string;
	amount: number;
	currency: 'xp' | 'gems';
	receipt?: unknown;
}): void {
	const userId = auth.currentUser?.uid ?? null;
	if (!userId) return;
	if (!Number.isFinite(args.amount) || args.amount <= 0) return;

	const payload = {
		amount: Math.abs(args.amount),
		currency: args.currency,
		...(args.receipt !== undefined ? { receipt: args.receipt } : {}),
	};

	const op: Omit<Operation, 'id' | 'clientTimestamp' | 'status'> = {
		userId,
		deviceId,
		type: args.type,
		entityId: args.entityId,
		payload,
	};

	void sync
		.record(op)
		.then(() => sync.sync())
		.catch((err: unknown) => {
			console.warn('[sync] recordEconomy failed', args.type, err);
		});
}

// ── Auto-sync triggers ───────────────────────────────────────────────────────
// Push whatever is queued when we (re)gain connectivity, and once on load.
// Kept deliberately simple; a debounced/interval sync can layer on top later.

let started = false;

export function startAutoSync(): void {
	if (started) return;
	started = true;

	// Sync on regaining network, but only while signed in. After the push/pull
	// catch-up, reconcile authoritative balances (economy.* never round-trips
	// the op log, so this is the only way to learn cross-device XP/gems).
	network.subscribe((state: NetworkState) => {
		if (state === 'online' && auth.currentUser) {
			void sync
				.sync()
				.then(() => reconcileBalances())
				.catch((err: unknown) => {
					// Swallow: offline-first means a failed sync is retried later,
					// not surfaced as an error to the user.
					console.warn('[sync] background sync failed', err);
				});
		}
	});

	// Initial catch-up once auth resolves. Push the one-time legacy economy
	// snapshot FIRST (so the opening balance is seeded before the engine starts
	// reconciling), then run the normal catch-up sync, then reconcile balances
	// so the header reflects the server total on load.
	const unsub = auth.onAuthStateChanged((user: User | null) => {
		if (user) {
			void runMigrationOnce()
				.catch((err: unknown) => {
					console.warn('[sync] migration push errored', err);
				})
				.then(() => sync.sync())
				.then(() => reconcileBalances())
				.catch((err: unknown) => {
					console.warn('[sync] initial sync failed', err);
				});
		}
	});

	// Keep the unsubscribe reachable for teardown in tests/HMR.
	(startAutoSync as unknown as { _unsub?: () => void })._unsub = unsub;
}
